import InfoCircle from "@iewcasia/MobiusOverrides/Icons/InfoCircle";
import { colorStrings } from "@iewcasia/Utilities/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconPresentationProps, IconWrapper, IconWrapperProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";

interface Props {
    product?: ProductModel;
    cartLine?: CartLineModel;
    showWrapper?: boolean;
    showIcon?: boolean;
    customUom?: string;
    extendedStyles?: MinimumOrderAndMultiplesOfInformationStyles;
}

export interface MinimumOrderAndMultiplesOfInformationStyles {
    wrapper?: InjectableCss;
    iconWrapper?: IconWrapperProps;
    icon?: IconPresentationProps;
    message?: TypographyPresentationProps;
}

export const minimumOrderAndMultiplesOfInformationStyles: MinimumOrderAndMultiplesOfInformationStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalGray06)};
            border: 1px solid ${getColor(colorStrings.commonBorder)};
            display: inline-flex;
            flex-direction: column;
            gap: 5px;
            justify-content: center;
            margin-bottom: 10px;
            min-height: 40px;
            padding: 5px 10px;
            width: 100%;
        `,
    },
    icon: {
        src: InfoCircle,
        css: css`
            margin-left: 1px;
            margin-right: 10px;
        `,
    },
    message: {
        variant: "p",
        color: colorStrings.commonGlobalGray02,
        css: css`
            margin: 0;
        `,
    },
};

const MinimumOrderAndMultiplesOfInformation = ({
    product,
    cartLine,
    showWrapper = true,
    showIcon,
    customUom,
    extendedStyles,
}: Props) => {
    const [styles] = useState(() => mergeToNew(minimumOrderAndMultiplesOfInformationStyles, extendedStyles));

    let minimumOrderQty = 0;
    let multiplesOfQty = 0;
    let uom = "";

    if (!product && !cartLine) {
        return null;
    }

    if (product) {
        minimumOrderQty =
            product.minimumOrderQty ??
            +(product.properties["ecommMOQ"] ?? product.properties["minimumOrderQty"] ?? "0");
        multiplesOfQty =
            product.detail?.multipleSaleQty ??
            +(product.properties["incrementalEcommMOQ"] ?? product.properties["multipleSaleQty"] ?? "0");
        uom = product.unitOfMeasures?.[0]?.unitOfMeasureDisplay ?? customUom ?? "";
    } else if (cartLine) {
        minimumOrderQty = +(cartLine.properties["product_minimumOrderQty"] ?? "0");
        multiplesOfQty = +(cartLine.properties["product_multipleSalesQty"] ?? "0");
        uom = cartLine.properties["baseUnitOfMeasure"] ?? customUom ?? "";
    }

    if (minimumOrderQty <= 1 && multiplesOfQty <= 1) {
        return null;
    }

    const content = (
        <>
            {minimumOrderQty > 1 && (
                <IconWrapper {...styles.iconWrapper}>
                    {showIcon && <Icon {...styles.icon} />}
                    <Typography {...styles.message}>{`${translate(
                        "Minimum Order",
                    )}: ${minimumOrderQty} ${uom}`}</Typography>
                </IconWrapper>
            )}

            {multiplesOfQty > 1 && (
                <IconWrapper {...styles.iconWrapper}>
                    {showIcon && <Icon {...styles.icon} />}
                    <Typography {...styles.message}>{`${translate(
                        "Purchase in Multiples of",
                    )}: ${multiplesOfQty} ${uom}`}</Typography>
                </IconWrapper>
            )}
        </>
    );

    if (showWrapper) {
        return <StyledWrapper {...styles.wrapper}>{content}</StyledWrapper>;
    }

    return content;
};

export default MinimumOrderAndMultiplesOfInformation;
